// Live streaming run view. fn-1.29.
//
// **Acceptance**:
//   - "Opening `/runs/:id` shows agent messages incrementally as MA
//     events arrive."
//   - "Reconnect on Wi-Fi drop replays from `Last-Event-ID`." —
//     handled by the browser's `EventSource` semantics (the wrapper
//     in `streams/session-stream.ts` is transparent on reconnect).
//
// **Wiring**:
//   - Initial history: `trpc.runs.events.useQuery({ sessionId })` — one
//     shot fetch on mount. Seeds the reducer so prior turns paint
//     instantly even when the SSE handshake takes 1-2s.
//   - Live stream: `useSessionStream({ sessionId, seedEvents })` over
//     the BFF SSE re-fan. The hook handles reducer plumbing.
//   - Send-message: `trpc.runs.sendMessage.useMutation()`; on success
//     refetch nothing (the next SSE frame carries the user.message +
//     subsequent agent turns).
//   - Approve / reject the inline pending card: `trpc.approvals.decide
//     .useMutation()` driven by the gated tool's matching approval row.
//     We resolve approval-id via `trpc.approvals.list` filtered by
//     session id — see `usePendingApprovalForSession`.
//
// **Design (Smokeball console redesign)**: the transcript is the core
// screen — a bounded card (transcript left, 320px context rail right)
// styled with the shared design-system primitives (.card/.tag/.btn/
// .avatar/.crumbs/.matter-grid) plus page-scoped `rd-*` classes in the
// colocated `run-detail.css`. This is a RESTYLE: all streaming/HITL/
// composer behaviour (SSE hook, Last-Event-ID resume, message reducer,
// approval + ask-user + send mutations, optimistic bubble, auto-scroll)
// is preserved verbatim — only the presentation changed.

import { MarkdownContent } from '@sb-ai-platform/ui-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { bffEndpoints } from '../../auth/bff-origin.js';
import {
  langfuseConfigured,
  maSessionUrl,
  openLangfuseTrace,
} from '../../config/observability-links.js';
import { SbIcon } from '../../design/sb-icon.js';
import { useClientOtel, useTrackUserAction } from '../../instrumentation/provider.js';
import { buildSessionStreamUrl } from '../../streams/session-stream.js';
import {
  type ChatMessage,
  type PendingApproval,
  type SessionEventOnWire,
  useSessionStream,
} from '../../streams/use-session-stream.js';
import { trpc } from '../../trpc-client.js';
import { AgentToolsPanel, readToolsetPosture } from '../agents/agent-tools-panel.js';
import {
  formatClockTime,
  formatDelta,
  formatTime,
  sessionStatusLabel,
  truncateToolName,
} from '../format.js';
import './run-detail.css';

/**
 * Cross-origin SSE (fn-45.4): in deployed mode the EventSource must open
 * against the BFF origin (where the `__Host-` cookies live), not the static
 * SPA origin. We build the stream URL off `bffEndpoints().sseBaseUrl`
 * (absolute in deployed mode, relative via the Vite proxy in dev).
 */
function buildBffSessionStreamUrl(sessionId: string): string {
  return buildSessionStreamUrl(sessionId, bffEndpoints().sseBaseUrl);
}

export function RunDetailPage(): React.ReactElement {
  const params = useParams<{ id: string }>();
  const sessionId = params.id ?? null;
  if (sessionId === null || sessionId.length === 0) {
    return (
      <div className="rd-page">
        <div className="page__header">
          <h1 className="page__title">Session</h1>
        </div>
        <p className="caption" role="alert">
          Missing session id.
        </p>
      </div>
    );
  }
  return <RunDetailInner sessionId={sessionId} />;
}

type Tab = 'transcript' | 'debug';

/** A status pill descriptor derived from the live session status. */
export interface StatusTag {
  readonly label: string;
  readonly variant: 'cyan' | 'grey' | 'yellow' | 'orange' | 'green';
  /** Render the pulsing brand dot (the "Running" tag). */
  readonly pulsing?: boolean;
}

/**
 * Map the live stream status + pending-approval flag to the header status
 * tags. Pure + exported so the mapping is unit-testable without mounting the
 * page. Mirrors the page's EXISTING status semantics (running / idle /
 * terminated from the reducer; "Needs you" == an unresolved HITL gate).
 */
export function deriveStatusTags(
  status: string,
  hasPendingApproval: boolean,
): readonly StatusTag[] {
  const tags: StatusTag[] = [];
  switch (status) {
    case 'running':
      tags.push({ label: 'Running', variant: 'cyan', pulsing: true });
      break;
    case 'idle':
      tags.push({ label: 'Idle', variant: 'grey' });
      break;
    case 'terminated':
      tags.push({ label: 'Ended', variant: 'grey' });
      break;
    case 'pending':
    case 'initializing':
      tags.push({ label: sessionStatusLabel(status), variant: 'yellow' });
      break;
    default:
      // 'unknown' (pre-first-frame) and any future status: no primary tag.
      break;
  }
  if (hasPendingApproval) {
    tags.push({ label: 'Needs you', variant: 'orange' });
  }
  return tags;
}

/** Short local time-of-day ("2:49 PM") for message + caption timestamps. */
function compactTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

/** Derive a display name from the session's actor_identity (email local part). */
function callerDisplay(actorIdentity: string | null | undefined): string | null {
  if (typeof actorIdentity !== 'string') return null;
  const trimmed = actorIdentity.trim();
  if (trimmed.length === 0) return null;
  const at = trimmed.indexOf('@');
  return at > 0 ? trimmed.slice(0, at) : trimmed;
}

/** Up-to-two-letter avatar initials from a display name. */
function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/[\s._@-]+/)
    .filter((p) => p.length > 0);
  const first = parts[0];
  if (first === undefined) return 'U';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase();
}

/** Read the pinned model id from the agent's freeform config JSON, if present. */
function readModel(config: Record<string, unknown> | null | undefined): string | null {
  if (config === null || config === undefined || typeof config !== 'object') return null;
  const model = (config as { model?: unknown }).model;
  return typeof model === 'string' && model.length > 0 ? model : null;
}

/** Basename of a mount path for the compact "Files" rail list. */
function basename(path: string): string {
  const parts = path.split('/').filter((p) => p.length > 0);
  const last = parts[parts.length - 1];
  return last ?? path;
}

/**
 * Read the public-safe `resolved_skills` projection off session metadata
 * (`{skill_name, ma_skill_id, ...}[]`, stamped at session-create by the CP).
 * Returns the skill names, or `[]` when absent/malformed.
 */
function readResolvedSkills(metadata: Record<string, unknown> | null): readonly string[] {
  if (metadata === null) return [];
  const raw = (metadata as { resolved_skills?: unknown }).resolved_skills;
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const skill of raw) {
    if (skill !== null && typeof skill === 'object') {
      const name = (skill as { skill_name?: unknown }).skill_name;
      if (typeof name === 'string' && name.length > 0) out.push(name);
    }
  }
  return out;
}

/** A mounted-file display entry: stable react key + human label. */
interface MountedFileEntry {
  readonly key: string;
  readonly label: string;
}

/**
 * Read the platform-stamped `mounted_files` (`{file_id, mount_path}[]`) off
 * session metadata and return a display label per file (mount basename, else
 * the file id) with a stable react key (the file id, else the mount path).
 * Returns `[]` when absent/malformed.
 */
function readMountedFiles(metadata: Record<string, unknown> | null): readonly MountedFileEntry[] {
  if (metadata === null) return [];
  const raw = (metadata as { mounted_files?: unknown }).mounted_files;
  if (!Array.isArray(raw)) return [];
  const out: MountedFileEntry[] = [];
  for (const file of raw) {
    if (file !== null && typeof file === 'object') {
      const mountPath = (file as { mount_path?: unknown }).mount_path;
      const fileId = (file as { file_id?: unknown }).file_id;
      const hasMount = typeof mountPath === 'string' && mountPath.length > 0;
      const hasId = typeof fileId === 'string' && fileId.length > 0;
      if (!hasMount && !hasId) continue;
      const label = hasMount ? basename(mountPath) : (fileId as string);
      const key = hasId ? (fileId as string) : (mountPath as string);
      out.push({ key, label });
    }
  }
  return out;
}

function RunDetailInner({ sessionId }: { readonly sessionId: string }): React.ReactElement {
  const sessionQuery = trpc.runs.byId.useQuery({ sessionId });
  const historyQuery = trpc.runs.events.useQuery({ sessionId });
  // fn-2.x — fetch the agent record so we can render its tool posture
  // (allow / deny / asks) + resolve its display name/model. Enabled once
  // we know the agent id from the session row.
  const agentId = sessionQuery.data?.agent_id;
  const agentQuery = trpc.agents.byId.useQuery(
    { agentId: agentId ?? '' },
    { enabled: agentId !== undefined && agentId.length > 0 },
  );

  // Memoize seed-events reference so the hook doesn't re-seed on every
  // render (would be a no-op via id dedup, but keeps React DevTools
  // honest).
  const seedEvents = useMemo(() => historyQuery.data?.data ?? [], [historyQuery.data]);

  const stream = useSessionStream({
    sessionId,
    seedEvents,
    // Open the EventSource against the BFF origin in deployed mode.
    urlBuilder: buildBffSessionStreamUrl,
  });

  const [tab, setTab] = useState<Tab>('transcript');
  // Per-run toggle: render assistant markdown (default) vs show the raw
  // Markdown source. Applies to the whole transcript, not per message.
  const [markdownSource, setMarkdownSource] = useState(false);
  // The page is not keyed by session id, so navigating /runs/:a -> /runs/:b
  // re-renders this component in place without unmounting. Reset the toggle on
  // run change so it stays genuinely per-run (defaults to rendered). sessionId
  // is the reset trigger (not read in the body), so exhaustive-deps would
  // wrongly strip it — hence the scoped suppression.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sessionId is the per-run reset trigger, not read in the effect body
  useEffect(() => {
    setMarkdownSource(false);
  }, [sessionId]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const selectedEvent = selectedEventId
    ? (stream.events.find((e) => e.id === selectedEventId) ?? null)
    : null;
  // fn-2.2 — bootstrap visibility toggle. The CP auto-injects a
  // `<system-bootstrap>` user.message on session create (carrying any
  // pasted CLAUDE.md / context document); the agent typically responds
  // with an ack ("I have processed the context…"). Both rows clutter
  // the operator's view of the real conversation, so hide them by
  // default and offer a toggle for debug.
  const [showBootstrap, setShowBootstrap] = useState(false);
  // fn-2.x — optimistic in-flight user message. The composer pushes
  // here on send; we clear when the real user.message lands on the
  // stream (matched by content). Avoids the visible 2–5 s gap
  // between "Send" and the SSE-delivered user.message that used to
  // make the SPA feel sluggish on the first turn of a session.
  const [pendingUserMessage, setPendingUserMessage] = useState<string | null>(null);
  useEffect(() => {
    if (pendingUserMessage === null) return;
    const matched = stream.messages.some(
      (m) => m.kind === 'user' && m.content.trim() === pendingUserMessage.trim(),
    );
    if (matched) setPendingUserMessage(null);
  }, [stream.messages, pendingUserMessage]);

  const visibleMessages = useMemo<readonly ChatMessage[]>(() => {
    const filtered = showBootstrap
      ? stream.messages
      : stream.messages.filter(
          (m) =>
            !(m.kind === 'user' && m.isBootstrap === true) &&
            !(m.kind === 'assistant' && m.isBootstrapAck === true),
        );
    // fn-73 — when "Show bootstrap" is ON, reveal the fn-72 folded grounding
    // block INLINE in its (first) user message: render `<system-bootstrap>…`
    // then the real text. When OFF, the message shows only its real text (the
    // block stays stripped). A bootstrap-ONLY legacy turn has empty text, so it
    // simply renders the block.
    const base = showBootstrap
      ? filtered.map((m) =>
          m.kind === 'user' && typeof m.bootstrapBlock === 'string'
            ? {
                ...m,
                content:
                  m.content.length > 0 ? `${m.bootstrapBlock}\n\n${m.content}` : m.bootstrapBlock,
              }
            : m,
        )
      : filtered;
    if (pendingUserMessage === null) return base;
    return [
      ...base,
      {
        kind: 'user' as const,
        id: '__pending__',
        content: pendingUserMessage,
      },
    ];
  }, [stream.messages, showBootstrap, pendingUserMessage]);
  const hasBootstrap = stream.messages.some(
    (m) =>
      (m.kind === 'user' && m.isBootstrap === true) ||
      (m.kind === 'assistant' && m.isBootstrapAck === true) ||
      // fn-73 — a fn-72 folded first message carries the grounding block here
      // (it is NOT flagged isBootstrap); the toggle must still appear for it.
      (m.kind === 'user' && typeof m.bootstrapBlock === 'string'),
  );

  // Distinct MCP servers actually exercised this session (from the
  // tool_use rows the reducer produced). Drives the "Used in this
  // session" rail card — live session data, not agent config.
  const mcpServers = useMemo(() => {
    const seen = new Set<string>();
    for (const m of stream.messages) {
      if (m.kind === 'tool_use' && m.mcpServerName !== undefined) seen.add(m.mcpServerName);
    }
    return [...seen];
  }, [stream.messages]);

  const session = sessionQuery.data;
  const metadata = session?.metadata ?? null;
  const agentDisplay =
    agentQuery.data?.name ?? (agentId !== undefined ? `${agentId.slice(0, 8)}…` : 'Agent');
  const userName = callerDisplay(session?.actor_identity) ?? 'You';
  const model = readModel(agentQuery.data?.config);
  const rawTitle =
    metadata !== null && typeof metadata.title === 'string' ? metadata.title.trim() : '';
  const title = rawTitle.length > 0 ? rawTitle : `Session ${sessionId.slice(0, 8)}`;
  const statusTags = deriveStatusTags(stream.status, stream.pendingApproval !== null);
  const maHref = maSessionUrl(session?.provider_session_ref);
  const connLabel =
    stream.connection === 'open'
      ? 'Live'
      : stream.connection === 'connecting'
        ? 'Connecting…'
        : 'Disconnected';

  // "Used in this session" rail card — surfaced only when the page has
  // real data for it (metadata.resolved_skills, metadata.mounted_files,
  // or MCP servers seen on the transcript).
  const skills = readResolvedSkills(metadata);
  const files = readMountedFiles(metadata);
  const hasUsedData = skills.length > 0 || files.length > 0 || mcpServers.length > 0;
  const posture = readToolsetPosture(agentQuery.data?.config ?? null);

  // Caption: "Started <time> by <user> · <agent> · <model>" — only the
  // fields the page actually has (omit the rest).
  const caller = callerDisplay(session?.actor_identity);
  const captionParts: string[] = [];
  if (session?.created_at !== undefined) {
    captionParts.push(
      `Started ${compactTime(session.created_at)}${caller !== null ? ` by ${caller}` : ''}`,
    );
  } else if (caller !== null) {
    captionParts.push(`Started by ${caller}`);
  }
  captionParts.push(agentDisplay);
  if (model !== null) captionParts.push(model);

  return (
    <div className="rd-page">
      <div className="crumbs">
        <Link to="/runs">Sessions</Link>
        <span className="sep">/</span>
        {agentId !== undefined ? (
          <Link to={`/agents/${encodeURIComponent(agentId)}`} title={`View agent ${agentId}`}>
            {agentDisplay}
          </Link>
        ) : (
          <span>{agentDisplay}</span>
        )}
        <span className="sep">/</span>
        <span className="here rd-crumb-id">#{sessionId.slice(0, 8)}</span>
      </div>

      <div className="page__header rd-header">
        <div>
          <div className="rd-title-row">
            <h1 className="page__title rd-title">{title}</h1>
            {statusTags.map((t) => (
              <StatusTagEl key={t.label} tag={t} />
            ))}
          </div>
          <div className="caption rd-subcaption">{captionParts.join(' · ')}</div>
        </div>
        <div className="page__actions rd-actions">
          <span className={`rd-conn rd-conn--${stream.connection}`} title={`Stream ${connLabel}`}>
            <span className="rd-conn-dot" aria-hidden="true" />
            <span className="caption">{connLabel}</span>
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--xs rd-toggle"
            aria-pressed={tab === 'debug'}
            onClick={() => setTab(tab === 'debug' ? 'transcript' : 'debug')}
            title="Show the raw MA event log"
          >
            <SbIcon name="list-timeline" size={14} />
            Raw events
          </button>
          {tab === 'transcript' && (
            <button
              type="button"
              className="btn btn--ghost btn--xs rd-toggle"
              aria-pressed={markdownSource}
              onClick={() => setMarkdownSource((v) => !v)}
              title="Show the raw Markdown source instead of the rendered output"
            >
              Markdown source
            </button>
          )}
          {tab === 'transcript' && hasBootstrap && (
            <button
              type="button"
              className="btn btn--ghost btn--xs rd-toggle"
              aria-pressed={showBootstrap}
              onClick={() => setShowBootstrap((v) => !v)}
              title="Show the auto-injected context message + agent ack"
            >
              Show bootstrap
            </button>
          )}
          {langfuseConfigured() && (
            <button
              type="button"
              className="btn btn--ghost btn--xs"
              onClick={() => void openLangfuseTrace(sessionId)}
              title="Open this session's trace in Langfuse"
            >
              <SbIcon name="external-link" size={14} />
              Langfuse
            </button>
          )}
          {maHref !== undefined && (
            <a
              className="btn btn--ghost btn--xs"
              href={maHref}
              target="_blank"
              rel="noopener noreferrer"
              title="Open the managed-agents session (platform.claude.com)"
            >
              <SbIcon name="external-link" size={14} />
              MA session
            </a>
          )}
        </div>
      </div>

      {stream.lastError !== null && (
        <div className="rd-error" role="alert">
          <strong>{stream.lastError.type}</strong>: {stream.lastError.message}
        </div>
      )}

      <div className="matter-grid rd-grid" style={{ gridTemplateColumns: '1fr 320px' }}>
        <div className="card rd-transcript-card">
          <div className="rd-messages">
            {tab === 'transcript' ? (
              <TranscriptView
                messages={visibleMessages}
                events={stream.events}
                selectedEventId={selectedEventId}
                onSelect={(id) => setSelectedEventId(id === selectedEventId ? null : id)}
                markdownSource={markdownSource}
                agentName={agentDisplay}
                userName={userName}
              />
            ) : (
              <DebugView
                events={stream.events}
                selectedEventId={selectedEventId}
                onSelect={(id) => setSelectedEventId(id === selectedEventId ? null : id)}
              />
            )}
            <AgentThinkingIndicator
              status={stream.status}
              messages={visibleMessages}
              pendingApproval={stream.pendingApproval}
            />
            {stream.pendingApproval !== null && (
              <PendingApprovalCard
                sessionId={sessionId}
                toolUseId={stream.pendingApproval.toolUseId}
                toolName={stream.pendingApproval.toolName}
                input={stream.pendingApproval.input}
              />
            )}
          </div>
          <RunComposer
            sessionId={sessionId}
            disabled={stream.status === 'terminated' || stream.pendingApproval !== null}
            disabledReason={
              stream.status === 'terminated'
                ? 'Session terminated'
                : stream.pendingApproval !== null
                  ? 'Approve or reject the pending tool to continue'
                  : null
            }
            onOptimisticSend={(c) => setPendingUserMessage(c)}
            onOptimisticClear={() => setPendingUserMessage(null)}
          />
        </div>

        <div className="rd-rail">
          {selectedEvent !== null && (
            <aside className="card rd-rail-card" aria-label="Event details">
              <div className="rd-inspector-head">
                <span className="tag tag--grey rd-mono">{selectedEvent.type}</span>
                <button
                  type="button"
                  className="rd-inspector-close"
                  onClick={() => setSelectedEventId(null)}
                  aria-label="Close event details"
                >
                  ×
                </button>
              </div>
              <div className="caption" style={{ marginBottom: 8 }}>
                Event ID: <span className="rd-mono">{selectedEvent.id}</span>
              </div>
              {selectedEvent.data !== undefined && selectedEvent.data !== null && (
                <pre className="rd-inspector-pre">
                  {JSON.stringify(selectedEvent.data, null, 2)}
                </pre>
              )}
            </aside>
          )}

          <div className="card rd-rail-card">
            <h3>Context</h3>
            <div className="row">
              <span className="k">Agent</span>
              <span className="v">
                {agentId !== undefined ? (
                  <Link to={`/agents/${encodeURIComponent(agentId)}`}>{agentDisplay}</Link>
                ) : (
                  agentDisplay
                )}
              </span>
            </div>
            {session?.matter_id !== undefined && session.matter_id !== null && (
              <div className="row">
                <span className="k">Matter</span>
                <span className="v rd-mono" title={session.matter_id}>
                  {session.matter_id}
                </span>
              </div>
            )}
            {model !== null && (
              <div className="row">
                <span className="k">Model</span>
                <span className="v rd-mono">{model}</span>
              </div>
            )}
            <div className="row">
              <span className="k">Session</span>
              <span className="v rd-mono">{sessionId}</span>
            </div>
            {session?.provider_session_ref !== undefined &&
              session.provider_session_ref !== null && (
                <div className="row">
                  <span className="k">MA ID</span>
                  <span className="v rd-mono">{session.provider_session_ref}</span>
                </div>
              )}
            {session?.created_at !== undefined && (
              <div className="row">
                <span className="k">Started</span>
                <span className="v">{formatTime(session.created_at)}</span>
              </div>
            )}
            {(() => {
              // Token usage row (design 1d's "Tokens" rail stat). Live
              // stream aggregate when events carried usage; falls back to
              // the durable sessions.cost_used_tokens column so a cold
              // (non-streaming) view still shows the last known count.
              const streamTotal =
                stream.tokens.input +
                stream.tokens.output +
                stream.tokens.cacheRead +
                stream.tokens.cacheCreate;
              const total =
                streamTotal > 0
                  ? streamTotal
                  : typeof session?.cost_used_tokens === 'number'
                    ? session.cost_used_tokens
                    : 0;
              if (total <= 0) return null;
              const detail =
                streamTotal > 0
                  ? `↓ in ${stream.tokens.input.toLocaleString()} · ↑ out ${stream.tokens.output.toLocaleString()} · cache read ${stream.tokens.cacheRead.toLocaleString()} · cache write ${stream.tokens.cacheCreate.toLocaleString()}`
                  : 'Durable count from sessions.cost_used_tokens';
              return (
                <div className="row">
                  <span className="k">Tokens</span>
                  <span className="v rd-mono" title={detail}>
                    {total.toLocaleString()}
                  </span>
                </div>
              );
            })()}
            <div className="row">
              <span className="k">Status</span>
              <span className="v" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {statusTags.length > 0 ? (
                  statusTags.map((t) => <StatusTagEl key={t.label} tag={t} />)
                ) : (
                  <span>{sessionStatusLabel(stream.status)}</span>
                )}
              </span>
            </div>
          </div>

          {hasUsedData && (
            <div className="card rd-rail-card">
              <h3>Used in this session</h3>
              {skills.length > 0 && (
                <>
                  <div className="caption-semibold rd-used-label">Skills</div>
                  <div className="rd-tag-row">
                    {skills.map((s) => (
                      <span key={s} className="tag tag--grey">
                        <SbIcon name="magic-wand" size={11} />
                        {s}
                      </span>
                    ))}
                  </div>
                </>
              )}
              {mcpServers.length > 0 && (
                <>
                  <div className="caption-semibold rd-used-label">MCP servers</div>
                  <div className="rd-tag-row">
                    {mcpServers.map((s) => (
                      <span key={s} className="tag tag--grey">
                        <SbIcon name="plug" size={11} />
                        {s}
                      </span>
                    ))}
                  </div>
                </>
              )}
              {files.length > 0 && (
                <>
                  <div className="caption-semibold rd-used-label">Files</div>
                  <div className="rd-files">
                    {files.map((f) => (
                      <span key={f.key} className="rd-file-row">
                        <SbIcon
                          name="file-text"
                          size={14}
                          style={{ color: 'var(--color-icon-subtle)' }}
                        />
                        {f.label}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {posture.length > 0 && agentQuery.data != null && (
            <div className="card rd-rail-card">
              <h3>Tool posture</h3>
              <AgentToolsPanel agent={agentQuery.data} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Header/rail status pill, restyled to the design-system `.tag` primitives. */
function StatusTagEl({ tag }: { readonly tag: StatusTag }): React.ReactElement {
  return (
    <span className={`tag tag--${tag.variant}`}>
      {tag.pulsing === true && (
        <span className="rd-status-dot rd-status-dot--pulse" aria-hidden="true" />
      )}
      {tag.label}
    </span>
  );
}

interface AgentThinkingIndicatorProps {
  readonly status: string;
  readonly messages: readonly ChatMessage[];
  readonly pendingApproval: PendingApproval | null;
}

function AgentThinkingIndicator({
  status,
  messages,
  pendingApproval,
}: AgentThinkingIndicatorProps): React.ReactElement | null {
  // Show a subtle "Agent is thinking…" hint when the session is
  // running and the last message is from the user (or a tool result
  // we're waiting on the agent to react to). Suppressed during a HITL
  // gate — the approval card is the more useful indicator there.
  if (pendingApproval !== null) return null;
  if (status !== 'running') return null;
  const last = messages.length > 0 ? messages[messages.length - 1] : undefined;
  if (last !== undefined && last.kind === 'assistant') return null;
  return (
    // biome-ignore lint/a11y/useSemanticElements: role="status" is an ARIA live region (no exact HTML element equivalent); intentional.
    <div className="rd-typing" role="status" aria-live="polite">
      <span className="rd-typing-dot" aria-hidden="true" />
      <span>Agent is thinking…</span>
    </div>
  );
}

interface TranscriptViewProps {
  readonly messages: readonly ChatMessage[];
  readonly events: readonly SessionEventOnWire[];
  readonly selectedEventId: string | null;
  readonly onSelect: (id: string) => void;
  readonly markdownSource: boolean;
  readonly agentName: string;
  readonly userName: string;
}

function TranscriptView({
  messages,
  events,
  selectedEventId,
  onSelect,
  markdownSource,
  agentName,
  userName,
}: TranscriptViewProps): React.ReactElement {
  // Auto-scroll to bottom on new message. The scroll container is now the
  // `.rd-messages` region (bounded card), so `scrollIntoView` scrolls the
  // transcript internally while the composer stays pinned.
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef<string | null>(null);
  useEffect(() => {
    const lastMsg = messages.length > 0 ? messages[messages.length - 1] : undefined;
    const lastId = lastMsg?.id ?? null;
    if (lastId !== null && lastId !== lastIdRef.current) {
      lastIdRef.current = lastId;
      bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages]);

  if (messages.length === 0) {
    return <p className="rd-empty">Waiting for the first event…</p>;
  }
  // Lookup map for timing — message id mirrors the originating event id
  // (the reducer sets msg.id = ev.id). Use the event's createdAt to
  // render per-message timestamp + delta from the previous message.
  const createdAtByEventId = new Map(events.map((e) => [e.id, e.createdAt ?? '']));
  return (
    <ol className="rd-thread" data-testid="run-messages">
      {messages.map((msg, idx) => {
        const createdAt = createdAtByEventId.get(msg.id);
        const prev = idx > 0 ? messages[idx - 1] : undefined;
        const prevCreatedAt = prev ? createdAtByEventId.get(prev.id) : undefined;
        const delta = formatDelta(prevCreatedAt, createdAt);
        const time = createdAt !== undefined && createdAt.length > 0 ? compactTime(createdAt) : '';
        if (msg.kind === 'user' || msg.kind === 'assistant') {
          return (
            <ChatBubble
              key={msg.id}
              message={msg}
              name={msg.kind === 'user' ? userName : agentName}
              time={time}
              markdownSource={markdownSource}
              selected={msg.id === selectedEventId}
              onSelect={onSelect}
            />
          );
        }
        if (msg.kind === 'tool_use') {
          return <ToolRow key={msg.id} message={msg} delta={delta} />;
        }
        // tool_result / tool_confirmation — standalone rows (replay-race
        // fallback where the originating tool_use never landed). Rendered in
        // the same indented tool lane via MessageBody.
        return (
          <li key={msg.id} className="rd-tools">
            <div className="rd-tool">
              <div className="rd-tool-body" style={{ paddingTop: 12 }}>
                <MessageBody message={msg} />
              </div>
            </div>
          </li>
        );
      })}
      <div ref={bottomRef} aria-hidden="true" />
    </ol>
  );
}

interface ChatBubbleProps {
  readonly message: Extract<ChatMessage, { kind: 'user' | 'assistant' }>;
  readonly name: string;
  readonly time: string;
  readonly markdownSource: boolean;
  readonly selected: boolean;
  readonly onSelect: (id: string) => void;
}

/** A user or agent turn — avatar + name/time header + body content. */
function ChatBubble({
  message,
  name,
  time,
  markdownSource,
  selected,
  onSelect,
}: ChatBubbleProps): React.ReactElement {
  const isUser = message.kind === 'user';
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: pre-existing console UI debt — click-to-select message rows open the event inspector; keyboard affordance is a later presentation-layer concern.
    <li className="rd-bubble" aria-selected={selected} onClick={() => onSelect(message.id)}>
      {isUser ? (
        <span className="avatar avatar--md rd-avatar-user">{initialsOf(name)}</span>
      ) : (
        <span className="avatar avatar--md rd-avatar-agent">
          <SbIcon name="sparkles" size={16} />
        </span>
      )}
      <div className="rd-bubble-body">
        <div className="rd-bubble-head">
          <span className="rd-bubble-name">{name}</span>
          {time.length > 0 && <span className="caption">{time}</span>}
        </div>
        <div className="rd-bubble-text">
          <MessageBody message={message} markdownSource={markdownSource} />
        </div>
      </div>
    </li>
  );
}

interface ToolRowProps {
  readonly message: Extract<ChatMessage, { kind: 'tool_use' }>;
  readonly delta: string;
}

/**
 * A single tool call — an indented bordered row with a chevron that
 * expands/collapses the inline output. Preserves the reducer's tool-result
 * bundling, the ask_user_question compact summary, and the HITL decision
 * badge; only the presentation changed.
 */
function ToolRow({ message, delta }: ToolRowProps): React.ReactElement {
  const isError = message.result?.isError === true;
  // Default collapsed; auto-expand a failed call so the error is visible.
  const [open, setOpen] = useState(isError);
  const isAskUser = message.toolName === 'ask_user_question';
  const askInput = isAskUser ? parseAskUserInput(message.input) : null;
  const hasResult = message.result !== undefined;
  const iconName = message.mcpServerName !== undefined ? 'plug' : 'grid';
  const label =
    message.mcpServerName !== undefined
      ? `${message.mcpServerName} · ${truncateToolName(message.toolName)}`
      : truncateToolName(message.toolName);
  return (
    <li className="rd-tools">
      <div className="rd-tool">
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: pre-existing console UI debt — click-to-expand tool row; keyboard affordance is a later presentation-layer concern. */}
        <div className="rd-tool-head" onClick={() => setOpen((v) => !v)}>
          <span className="rd-tool-chevron">
            <SbIcon name={open ? 'chevron-down' : 'chevron-right'} size={12} />
          </span>
          <SbIcon
            name={iconName}
            size={14}
            style={{ color: 'var(--color-icon-subtle)', flexShrink: 0 }}
          />
          <span className="rd-tool-name" title={message.toolName}>
            {label}
          </span>
          {message.decision !== undefined && (
            <span
              className={`rd-tool-decision rd-tool-decision--${message.decision}`}
              title={
                message.decidedBy !== undefined
                  ? `${message.decision === 'allow' ? 'Approved' : 'Denied'} by ${message.decidedBy}${message.decidedAt !== undefined ? ` · ${formatTime(message.decidedAt)}` : ''}`
                  : undefined
              }
            >
              {message.decision === 'allow' ? '✓ Approved' : '✗ Denied'}
            </span>
          )}
          <span className="rd-tool-spacer" />
          {delta.length > 0 && (
            <span className="caption" title="Time since previous message">
              {delta}
            </span>
          )}
          {hasResult && (
            <SbIcon
              name={isError ? 'circle-exclamation-solid' : 'circle-check-solid'}
              size={15}
              style={{
                color: isError ? 'var(--color-text-danger)' : 'var(--color-text-success)',
                flexShrink: 0,
              }}
            />
          )}
        </div>
        {open && (
          <div className="rd-tool-body">
            {isAskUser && askInput !== null ? (
              <AskUserQuestionSummary
                questions={askInput}
                answers={
                  message.result !== undefined ? parseAskUserAnswers(message.result.output) : null
                }
              />
            ) : (
              <pre className="rd-pre">{JSON.stringify(message.input, null, 2)}</pre>
            )}
            {!isAskUser && message.result !== undefined && (
              <>
                <p className="rd-tool-result-label">{isError ? '✗ Error' : '↳ Output'}</p>
                <pre className={`rd-pre${isError ? ' rd-pre--error' : ''}`}>
                  {message.result.output}
                </pre>
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

interface DebugViewProps {
  readonly events: readonly SessionEventOnWire[];
  readonly selectedEventId: string | null;
  readonly onSelect: (id: string) => void;
}

function DebugView({ events, selectedEventId, onSelect }: DebugViewProps): React.ReactElement {
  if (events.length === 0) {
    return <p className="rd-empty">No events yet.</p>;
  }
  return (
    <ol className="rd-debug" data-testid="run-raw-events">
      {events.map((ev, idx) => {
        const prev = idx > 0 ? events[idx - 1] : undefined;
        const delta = formatDelta(prev?.createdAt, ev.createdAt);
        return (
          // biome-ignore lint/a11y/useKeyWithClickEvents: pre-existing console UI debt — click-to-select debug rows open the event inspector; keyboard affordance is a later presentation-layer concern.
          <li
            key={ev.id}
            className="rd-debug-row"
            aria-selected={ev.id === selectedEventId}
            onClick={() => onSelect(ev.id)}
          >
            <SbIcon name="list-timeline" size={12} style={{ color: 'var(--color-icon-subtle)' }} />
            <span>{ev.type}</span>
            <span className="rd-debug-meta">
              {ev.createdAt !== undefined && (
                <time dateTime={ev.createdAt} title={ev.createdAt}>
                  {formatClockTime(ev.createdAt)}
                </time>
              )}
              {delta.length > 0 && <span> · {delta}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * fn-2.x — collapsible disclosure for Claude's extended-thinking
 * content. Default hidden so the assistant turn renders compact;
 * one click reveals the model's pre-answer reasoning.
 */
function ThinkingDisclosure({ text }: { readonly text: string }): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className="rd-thinking">
      <button
        type="button"
        className="rd-thinking-toggle"
        onClick={(e) => {
          // Don't propagate to the message row's onSelect handler.
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
      >
        💭 {open ? 'Hide' : 'Show'} thinking ({text.length.toLocaleString()} chars)
      </button>
      {open && <pre className="rd-thinking-body">{text}</pre>}
    </div>
  );
}

/**
 * fn-2.x — compact transcript renderer for `ask_user_question`
 * tool_use rows. Shows the question count + the question texts as
 * a bullet list (collapsed by default for ≥3 questions so the
 * transcript stays tidy). When the operator's answers have been
 * submitted (result is set), shows them as a "Q → A" summary.
 *
 * Why a bespoke renderer: the raw input is a 50+ line JSON dump of
 * nested option objects; the wizard form below the row already
 * surfaces every option interactively, so duplicating that as JSON
 * adds noise without information.
 */
function AskUserQuestionSummary({
  questions,
  answers,
}: {
  readonly questions: ReadonlyArray<AskUserQuestionShape>;
  readonly answers: Record<string, string | readonly string[]> | null;
}): React.ReactElement {
  const [expanded, setExpanded] = useState(questions.length < 3);
  return (
    <div className="run-tool-askuser">
      <button
        type="button"
        className="run-tool-askuser-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        aria-expanded={expanded}
      >
        {expanded ? '▾' : '▸'} {questions.length} question{questions.length === 1 ? '' : 's'} asked
        {answers !== null && <span className="run-tool-askuser-answered"> · answered</span>}
      </button>
      {expanded && (
        <ol className="run-tool-askuser-list">
          {questions.map((q) => {
            const ans = answers?.[q.question];
            const ansText = Array.isArray(ans)
              ? ans.join(', ')
              : typeof ans === 'string'
                ? ans
                : null;
            return (
              <li key={q.question}>
                {q.header !== undefined && q.header.length > 0 && (
                  <span className="run-tool-askuser-header">{q.header}</span>
                )}
                <span className="run-tool-askuser-q">{q.question}</span>
                {ansText !== null && (
                  <div className="run-tool-askuser-a">
                    <span className="run-tool-askuser-a-arrow">↳</span>
                    <span className="run-tool-askuser-a-text">{ansText}</span>
                  </div>
                )}
                {ansText === null && (
                  <ul className="run-tool-askuser-options">
                    {q.options.map((o) => (
                      <li key={o.label} title={o.description}>
                        {o.label}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function parseAskUserInput(
  input: Record<string, unknown>,
): ReadonlyArray<AskUserQuestionShape> | null {
  const qs = (input as { questions?: unknown }).questions;
  if (!Array.isArray(qs)) return null;
  const out: AskUserQuestionShape[] = [];
  for (const q of qs) {
    if (q === null || typeof q !== 'object') continue;
    const qq = q as {
      question?: unknown;
      header?: unknown;
      options?: unknown;
      multiSelect?: unknown;
    };
    if (typeof qq.question !== 'string' || !Array.isArray(qq.options)) continue;
    const options: AskUserQuestionOption[] = [];
    for (const o of qq.options) {
      if (o === null || typeof o !== 'object') continue;
      const oo = o as { label?: unknown; description?: unknown };
      if (typeof oo.label !== 'string') continue;
      options.push({
        label: oo.label,
        ...(typeof oo.description === 'string' ? { description: oo.description } : {}),
      });
    }
    out.push({
      question: qq.question,
      options,
      ...(typeof qq.header === 'string' ? { header: qq.header } : {}),
      ...(typeof qq.multiSelect === 'boolean' ? { multiSelect: qq.multiSelect } : {}),
    });
  }
  return out.length > 0 ? out : null;
}

/**
 * The tool_result payload for ask_user_question is the form-data the
 * operator submitted, JSON-stringified by MA. Try to recover the
 * `answers` map; return null on any shape mismatch (compact card
 * silently falls back to the per-question option list).
 */
function parseAskUserAnswers(output: string): Record<string, string | readonly string[]> | null {
  try {
    const parsed = JSON.parse(output);
    const a = (parsed as { answers?: unknown }).answers;
    if (a === null || typeof a !== 'object' || Array.isArray(a)) return null;
    const result: Record<string, string | readonly string[]> = {};
    for (const [k, v] of Object.entries(a as Record<string, unknown>)) {
      if (typeof v === 'string') result[k] = v;
      else if (Array.isArray(v) && v.every((x) => typeof x === 'string')) {
        result[k] = v as readonly string[];
      }
    }
    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
}

/**
 * Message body content renderer. Handles the "content-only" kinds
 * (user / assistant / tool_result / tool_confirmation) — the tool_use
 * kind is rendered by `ToolRow`, which owns the tool-card chrome.
 * Exported for backwards compatibility with earlier fn-2.x wiring.
 */
export function MessageBody({
  message,
  markdownSource = false,
}: {
  readonly message: ChatMessage;
  /** When true, show the assistant's raw Markdown source instead of rendering it. */
  readonly markdownSource?: boolean;
}): React.ReactElement {
  switch (message.kind) {
    case 'user':
      // User input is literal text, never markdown-authored — keep it plain.
      return <p>{message.content}</p>;
    case 'assistant':
      return (
        <Fragment>
          {message.thinking !== undefined && message.thinking.length > 0 && (
            <ThinkingDisclosure text={message.thinking} />
          )}
          {markdownSource ? (
            <pre className="rd-md-source">{message.content}</pre>
          ) : (
            <MarkdownContent content={message.content} />
          )}
          {message.usage !== undefined &&
            (message.usage.input !== undefined || message.usage.output !== undefined) && (
              <small className="rd-usage" title="Token usage on this turn">
                {message.usage.input !== undefined && (
                  <span>↓ {message.usage.input.toLocaleString()} in</span>
                )}
                {message.usage.output !== undefined && (
                  <span>↑ {message.usage.output.toLocaleString()} out</span>
                )}
              </small>
            )}
        </Fragment>
      );
    case 'tool_result':
      return (
        <Fragment>
          <p className="rd-tool-result-label">
            {message.isError === true ? '✗ Error' : '↳ Output'}
          </p>
          <pre className={`rd-pre${message.isError === true ? ' rd-pre--error' : ''}`}>
            {message.output}
          </pre>
        </Fragment>
      );
    case 'tool_confirmation': {
      const verdict = message.decision === 'allow' ? 'Approved' : 'Denied';
      const who = message.decidedBy !== undefined ? ` by ${message.decidedBy}` : '';
      const when = message.decidedAt !== undefined ? ` at ${formatTime(message.decidedAt)}` : '';
      return (
        <p className="caption">
          {verdict}
          {who}
          {when}
          {message.denyMessage !== undefined ? ` — ${message.denyMessage}` : null}
        </p>
      );
    }
    default:
      // tool_use is rendered by ToolRow; this is defensive only.
      return <p>?</p>;
  }
}

interface PendingApprovalCardProps {
  readonly sessionId: string;
  readonly toolUseId: string;
  readonly toolName: string;
  readonly input: Record<string, unknown>;
}

/**
 * Pending HITL approval card. The control plane row id (approval id)
 * isn't carried in the MA event; we resolve it by listing the
 * session's pending approvals and matching `provider_tool_use_id`.
 *
 * **Why the lookup happens inline rather than on the stream-state**:
 * the approval row's id is a DB-generated UUID assigned when the HITL
 * daemon writes the row — independent of any MA event payload. The
 * SPA must call `approvals.list` to learn it. Doing so HERE (inside the
 * card) means the lookup only happens when a card is visible.
 */
function PendingApprovalCard(props: PendingApprovalCardProps): React.ReactElement {
  // Per-session listing (control plane has `GET /sessions/:id/approvals`,
  // but our BFF only exposes the cross-session `approvals.list`. Filter
  // client-side by session id.)
  const approvalsQuery = trpc.approvals.list.useQuery(
    {},
    {
      refetchInterval: 3_000,
    },
  );
  const approval = approvalsQuery.data?.data.find(
    (a) => a.session_id === props.sessionId && a.provider_tool_use_id === props.toolUseId,
  );
  const decideMutation = trpc.approvals.decide.useMutation();
  const utils = trpc.useUtils();
  const trackUserAction = useTrackUserAction();
  const otel = useClientOtel();
  // Optional free-text note attached to a Deny (the mutation's denyMessage).
  const [note, setNote] = useState('');
  // fn-1.31 round 7 — prime the platform-sessionId trace cache so the
  // approve/reject click emits its `spa.approval.*` span synchronously
  // on `hash(props.sessionId)`. Matches the wiring in
  // `pages/approvals/list.tsx`'s `ApprovalRow` — same correlator, same
  // cross-tier stitch. Fire-and-forget; idempotent.
  useEffect(() => {
    if (otel !== null) {
      void otel.primeSessionTrace(props.sessionId);
    }
  }, [otel, props.sessionId]);

  const handle = async (
    decision: 'approved' | 'rejected',
    denyMessage?: string,
    formData?: Record<string, unknown>,
  ): Promise<void> => {
    if (approval === undefined) return;
    // fn-1.31 round 7 — emit the SPA click span before the mutation,
    // anchored on the platform sessionId so the BFF + control-plane
    // spans nest on the same Langfuse trace. Matches the call shape in
    // `pages/approvals/list.tsx`'s `ApprovalRow`.
    trackUserAction({
      name: `spa.approval.${decision === 'approved' ? 'approve' : 'reject'}`,
      sessionId: props.sessionId,
      metadata: {
        approval_id: approval.id,
        session_id: props.sessionId,
        tool_name: props.toolName,
        tool_use_id: props.toolUseId,
      },
    });
    try {
      await decideMutation.mutateAsync({
        sessionId: props.sessionId,
        approvalId: approval.id,
        decision,
        ...(denyMessage !== undefined && denyMessage.length > 0 ? { denyMessage } : {}),
        ...(formData !== undefined ? { formData } : {}),
      });
      // Refresh the badge + list immediately rather than waiting for the
      // next poll tick. The session stream will receive the matching
      // `user.tool_confirmation` event via SSE in a moment.
      await Promise.all([
        utils.approvals.pendingCount.invalidate(),
        utils.approvals.list.invalidate(),
      ]);
    } catch {
      // Mutation error surfaces via decideMutation.error below.
    }
  };

  const isAskUser =
    props.toolName === 'ask_user_question' &&
    approval !== undefined &&
    approval.ack_kind === 'custom_tool_result_immediate';
  const actionsDisabled =
    approval === undefined || decideMutation.isPending || approvalsQuery.isPending;

  return (
    <section
      aria-label="Tool approval required"
      className="rd-approval"
      data-testid="pending-approval-card"
    >
      <div className="rd-approval-head">
        <SbIcon
          name="diamond-exclamation-solid"
          size={18}
          style={{ color: 'var(--color-text-warning)' }}
        />
        <span className="rd-approval-title">
          {isAskUser ? 'Response needed' : 'Approval needed'}
        </span>
        <span className="rd-approval-tool" title={props.toolName}>
          {props.toolName}
        </span>
      </div>
      {/* fn-2.x — render the ask_user_question form inline when the
          approval row is the elicitation kind. The control-plane
          `validateAskUserAnswers` accepts free-text per the Agent SDK
          "Other" pattern, so each question gets the declared options
          plus an "Other (type your own)" row with a text input. */}
      {isAskUser ? (
        <AskUserQuestionForm
          input={props.input}
          isPending={decideMutation.isPending}
          onSubmit={(formData) => void handle('approved', undefined, formData)}
          onReject={() => void handle('rejected')}
        />
      ) : (
        <>
          <p className="rd-approval-desc">The agent needs your approval to run this tool.</p>
          <pre className="rd-pre">{JSON.stringify(props.input, null, 2)}</pre>
          <div className="rd-approval-actions">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void handle('approved')}
              disabled={actionsDisabled}
            >
              Approve and run
            </button>
            <button
              type="button"
              className="btn btn--tertiary"
              onClick={() =>
                void handle('rejected', note.trim().length > 0 ? note.trim() : undefined)
              }
              disabled={actionsDisabled}
            >
              Deny
            </button>
            <input
              className="rd-note-input"
              placeholder="Add a note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={actionsDisabled}
            />
          </div>
          {approval === undefined && approvalsQuery.error === null && (
            <small className="rd-approval-note">Looking up approval id…</small>
          )}
          {approvalsQuery.error !== null && (
            <>
              <div className="rd-approval-error" role="alert">
                Failed to load approval id: {approvalsQuery.error.message}
              </div>
              <button
                type="button"
                className="btn btn--tertiary btn--xs"
                onClick={() => void approvalsQuery.refetch()}
                disabled={approvalsQuery.isFetching}
              >
                Retry
              </button>
            </>
          )}
        </>
      )}
      {decideMutation.error !== null && (
        <p className="rd-approval-error" role="alert">
          {decideMutation.error.message}
        </p>
      )}
    </section>
  );
}

/**
 * fn-2.x — `ask_user_question` elicitation form. Renders the agent's
 * 1-4 questions as radio (single-select) or checkbox (multi-select)
 * groups, plus an "Other (type your own)" row that accepts free text
 * per the Agent SDK convention. On submit, builds the
 * `{questions, answers}` payload validated by
 * `validateAskUserAnswers` (CP-side) and sends it as `formData` on
 * approvals.decide.
 *
 * Schema verified against the Agent SDK docs (2026-05-17):
 *   https://code.claude.com/docs/en/agent-sdk/user-input
 */
interface AskUserQuestionOption {
  readonly label: string;
  readonly description?: string;
}
interface AskUserQuestionShape {
  readonly question: string;
  readonly header?: string;
  readonly options: readonly AskUserQuestionOption[];
  readonly multiSelect?: boolean;
}
function AskUserQuestionForm({
  input,
  isPending,
  onSubmit,
  onReject,
}: {
  readonly input: Record<string, unknown>;
  readonly isPending: boolean;
  readonly onSubmit: (formData: Record<string, unknown>) => void;
  readonly onReject: () => void;
}): React.ReactElement {
  // fn-2.x (CodeRabbit) — use the same defensive parser the
  // transcript renderer uses (parseAskUserInput) so malformed
  // payloads can't crash the render path with a missing-options
  // `.map` or null-options access. Empty array → form bails to a
  // helpful "No questions" branch below.
  const questions = parseAskUserInput(input) ?? [];
  // Wizard step (0-indexed). Default to first question; advances via
  // Next button, reverts via Back. Last question's Next becomes Submit.
  const [step, setStep] = useState(0);
  // Per-question state. For single-select: a string (label or
  // free-text). For multi-select: a string array. The "Other" flag
  // tracks whether the operator picked the free-text row; the
  // accompanying text input value is stored independently so toggling
  // off Other doesn't wipe their typing.
  const [singleChoice, setSingleChoice] = useState<Record<string, string>>({});
  const [otherActive, setOtherActive] = useState<Record<string, boolean>>({});
  const [otherText, setOtherText] = useState<Record<string, string>>({});
  const [multiChoice, setMultiChoice] = useState<Record<string, Set<string>>>({});

  const isAnswered = (q: AskUserQuestionShape): boolean => {
    if (q.multiSelect === true) {
      const picks = multiChoice[q.question];
      const otherT = otherActive[q.question] === true ? (otherText[q.question] ?? '').trim() : '';
      const total = (picks?.size ?? 0) + (otherT.length > 0 ? 1 : 0);
      return total > 0;
    }
    if (otherActive[q.question] === true) {
      return (otherText[q.question] ?? '').trim().length > 0;
    }
    return (singleChoice[q.question] ?? '').length > 0;
  };
  const allAnswered = questions.every(isAnswered);
  const isLast = step === questions.length - 1;
  const currentQuestion = questions[step];
  const currentAnswered = currentQuestion !== undefined ? isAnswered(currentQuestion) : false;

  const buildPayload = (): Record<string, unknown> => {
    const answers: Record<string, string | readonly string[]> = {};
    for (const q of questions) {
      const otherT = otherActive[q.question] === true ? (otherText[q.question] ?? '').trim() : '';
      if (q.multiSelect === true) {
        const labels = [...(multiChoice[q.question] ?? new Set<string>())];
        if (otherT.length > 0) labels.push(otherT);
        answers[q.question] = labels;
      } else if (otherT.length > 0) {
        answers[q.question] = otherT;
      } else {
        answers[q.question] = singleChoice[q.question] ?? '';
      }
    }
    return { questions, answers };
  };

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!allAnswered) return;
    onSubmit(buildPayload());
  };

  if (currentQuestion === undefined) {
    return <p>No questions provided.</p>;
  }
  const q = currentQuestion;
  const inputName = `q-${step}`;

  return (
    <form className="ask-user-form" onSubmit={submit}>
      {/* Wizard progress: step n of N + dot indicator. */}
      <div className="ask-user-form-progress">
        <span className="ask-user-form-progress-text">
          Question {step + 1} of {questions.length}
        </span>
        <div className="ask-user-form-progress-dots" role="presentation">
          {questions.map((qq, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: each question slot is positional, the array is fixed-length per session
              key={i}
              className={`ask-user-form-progress-dot${
                i === step ? ' ask-user-form-progress-dot--active' : ''
              }${isAnswered(qq) ? ' ask-user-form-progress-dot--done' : ''}`}
            />
          ))}
        </div>
      </div>
      <fieldset className="ask-user-form-question">
        <legend className="ask-user-form-question-legend">
          {q.header !== undefined && q.header.length > 0 && (
            <span className="ask-user-form-question-header">{q.header}</span>
          )}
          <span className="ask-user-form-question-text">{q.question}</span>
          {q.multiSelect === true && (
            <span className="ask-user-form-question-hint">(pick one or more)</span>
          )}
        </legend>
        <div className="ask-user-form-options">
          {q.options.map((opt, oi) => {
            const optId = `${inputName}-opt-${oi}`;
            const isMulti = q.multiSelect === true;
            const checked = isMulti
              ? multiChoice[q.question]?.has(opt.label) === true
              : singleChoice[q.question] === opt.label;
            return (
              <label
                key={optId}
                htmlFor={optId}
                className={`ask-user-form-option${checked ? ' ask-user-form-option--checked' : ''}`}
              >
                <input
                  id={optId}
                  type={isMulti ? 'checkbox' : 'radio'}
                  name={inputName}
                  checked={checked}
                  onChange={(e) => {
                    if (isMulti) {
                      setMultiChoice((prev) => {
                        const next = { ...prev };
                        const set = new Set(next[q.question] ?? []);
                        if (e.target.checked) set.add(opt.label);
                        else set.delete(opt.label);
                        next[q.question] = set;
                        return next;
                      });
                    } else {
                      setSingleChoice((prev) => ({ ...prev, [q.question]: opt.label }));
                      setOtherActive((prev) => ({ ...prev, [q.question]: false }));
                    }
                  }}
                />
                <span className="ask-user-form-option-label">{opt.label}</span>
                {opt.description !== undefined && opt.description.length > 0 && (
                  <span className="ask-user-form-option-description">{opt.description}</span>
                )}
              </label>
            );
          })}
          {/* "Other" free-text row — Agent SDK convention. */}
          <label
            htmlFor={`${inputName}-other`}
            className={`ask-user-form-option ask-user-form-option--other${otherActive[q.question] === true ? ' ask-user-form-option--checked' : ''}`}
          >
            <input
              id={`${inputName}-other`}
              type={q.multiSelect === true ? 'checkbox' : 'radio'}
              name={inputName}
              checked={otherActive[q.question] === true}
              onChange={(e) => {
                setOtherActive((prev) => ({ ...prev, [q.question]: e.target.checked }));
                if (!q.multiSelect && e.target.checked) {
                  setSingleChoice((prev) => ({ ...prev, [q.question]: '' }));
                }
              }}
            />
            <span className="ask-user-form-option-label">Other (type your own)</span>
            {otherActive[q.question] === true && (
              <input
                type="text"
                className="ask-user-form-other-input"
                placeholder="Type your answer…"
                value={otherText[q.question] ?? ''}
                onChange={(e) =>
                  setOtherText((prev) => ({ ...prev, [q.question]: e.target.value }))
                }
              />
            )}
          </label>
        </div>
      </fieldset>
      <div className="ask-user-form-actions">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0 || isPending}
          className="ask-user-form-back"
        >
          ← Back
        </button>
        {!isLast ? (
          <button
            type="button"
            onClick={() => setStep((s) => Math.min(questions.length - 1, s + 1))}
            disabled={!currentAnswered || isPending}
            className="ask-user-form-next"
          >
            Next →
          </button>
        ) : (
          <button
            type="submit"
            disabled={isPending || !allAnswered}
            className="ask-user-form-submit"
          >
            {isPending ? 'Submitting…' : 'Submit answers'}
          </button>
        )}
        <button
          type="button"
          onClick={onReject}
          disabled={isPending}
          className="ask-user-form-reject"
          style={{ marginLeft: 'auto' }}
        >
          Reject
        </button>
      </div>
    </form>
  );
}

interface RunComposerProps {
  readonly sessionId: string;
  readonly disabled: boolean;
  readonly disabledReason: string | null;
  /**
   * fn-2.x — parent renders the pending user bubble immediately on
   * submit, before the BFF/CP round-trip + MA's SSE delivery
   * completes. Improves perceived latency on the first turn.
   */
  readonly onOptimisticSend?: (content: string) => void;
  /** Cleared if the mutation throws; the parent removes the bubble. */
  readonly onOptimisticClear?: () => void;
}

function RunComposer({
  sessionId,
  disabled,
  disabledReason,
  onOptimisticSend,
  onOptimisticClear,
}: RunComposerProps): React.ReactElement {
  const [content, setContent] = useState('');
  const sendMutation = trpc.runs.sendMessage.useMutation();
  const submit = async (): Promise<void> => {
    const trimmedContent = content.trim();
    if (trimmedContent.length === 0) return;
    onOptimisticSend?.(trimmedContent);
    setContent('');
    try {
      await sendMutation.mutateAsync({ sessionId, content: trimmedContent });
    } catch {
      // surfaces via sendMutation.error; also clear the pending
      // bubble so the operator can resubmit (or know it failed).
      onOptimisticClear?.();
    }
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter sends; Shift+Enter / Cmd+Enter inserts a newline. This is
    // the chat-first ergonomics most users expect (Anthropic Console,
    // Slack, etc.). Mod+Enter also sends as a power-user shortcut.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };
  const sendDisabled = disabled || sendMutation.isPending || content.trim().length === 0;
  return (
    <form
      className="rd-composer"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <label className="visually-hidden" htmlFor="run-composer-input">
        Message
      </label>
      <div className="rd-composer-row">
        <textarea
          id="run-composer-input"
          className="rd-composer-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            disabled
              ? (disabledReason ?? 'Composer disabled')
              : 'Reply or give instructions — Enter to send, Shift+Enter for newline'
          }
          disabled={disabled || sendMutation.isPending}
          rows={2}
        />
        <button
          type="submit"
          disabled={sendDisabled}
          className="btn btn--primary rd-send"
          aria-label={sendMutation.isPending ? 'Sending message' : 'Send message'}
        >
          <SbIcon name="paper-plane-top" size={15} />
        </button>
      </div>
      <div className="rd-composer-footer">
        <small className="rd-composer-hint">
          {sendMutation.isPending ? (
            <span>Sending…</span>
          ) : content.trim().length > 0 ? (
            <span>
              <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for newline ·{' '}
              {content.length.toLocaleString()} chars
            </span>
          ) : (
            <span>
              <kbd>Enter</kbd> to send · <kbd>Shift+Enter</kbd> for newline
            </span>
          )}
        </small>
      </div>
      {sendMutation.error !== null && (
        <p className="rd-composer-error" role="alert">
          {sendMutation.error.message}
        </p>
      )}
    </form>
  );
}
